import { useContext, useEffect, useState, useMemo } from "react"
import { PageHeader } from "../../components/shared/PageHeader"
import { TransactionsTable } from "../../components/table/Transactions/TransactionsTable"
import { PageHeaderControlContext } from "../../context/PageHeaderControlContext"
import { useData } from "../../utils/useData"
import { LoadingSpinner } from "../../components/shared/LoadingSpinner"
import { Transaction } from "../../models/transactions/transaction"
import { useCustomQuery } from "../../utils/useData"
import { TransactionType } from "../../models/transactions/transactionType"
import { TransactionsTableControlBar } from "../../components/table/Transactions/TransactionsTableControlBar"
import { useSearchParams } from "react-router-dom"
import { convertTransactionsToCsv, convertTransactionsToTaxLabFbtCsv, downloadCsv } from "../../utils/convertToCSV"
import { TransactionStatus } from "../../models/transactions/transactionStatus"
import { formatDateForQuery } from "../../utils/formatters"

// Helper function to build query strings
const buildQueryString = (params: Record<string, any>): string => {
    return Object.entries(params)
        .flatMap(([key, value]) => {
            if (value === undefined || value === null || value === "") {
                return []
            }
            // If the value is an array, map each value to the same key
            if (Array.isArray(value)) {
                return value.map(
                    (v) => `${encodeURIComponent(key)}=${encodeURIComponent(v)}`
                )
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`
        })
        .join("&")
}

// Helper function to format date as YYYY-MM-DD
const formatDateOnly = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

// Helper function to parse YYYY-MM-DD to Date
const parseDateOnly = (dateStr: string): Date | undefined => {
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? undefined : date;
};

// Helper function to get default start date (30 days ago at midnight UTC)
const getDefaultStartDate = (): Date => {
    const date = new Date();
    date.setUTCDate(date.getUTCDate() - 30);
    return new Date(Date.UTC(
        date.getUTCFullYear(),
        date.getUTCMonth(),
        date.getUTCDate(),
        0,
        0,
        0,
        0
    ));
};

const Transactions = () => {
    const { setHeaderActions } = useContext(PageHeaderControlContext)
    const { employer, employees, accounts, serviceSectors, plans, allEmployees, tagCategories } = useData()
    const [searchParams, setSearchParams] = useSearchParams()
    const [page, setPage] = useState(0)
    const [pageSize, setPageSize] = useState(10)

    // Initialize dates from URL parameters or default values
    const initialStartDate = searchParams.get('fromDate') 
        ? parseDateOnly(searchParams.get('fromDate')!) ?? getDefaultStartDate()
        : getDefaultStartDate();
    const initialEndDate = searchParams.get('toDate') 
        ? parseDateOnly(searchParams.get('toDate')!) 
        : undefined;
    
    const [startDate, setStartDate] = useState<Date>(initialStartDate);
    const [endDate, setEndDate] = useState<Date | undefined>(initialEndDate);
    const [searchTerm, setSearchTerm] = useState(searchParams.get('searchTerm') || '')
    const [selectedPlans, setSelectedPlans] = useState<string[]>(
        searchParams.get('planId')?.split(',').filter(Boolean) || []
    )

    // Set initial fromDate in URL if not present
    if (!searchParams.get('fromDate')) {
        const newParams = new URLSearchParams(searchParams);
        newParams.set('fromDate', formatDateOnly(initialStartDate));
        setSearchParams(newParams);
    }

    // Get distinct list of plans
    const distinctPlans = useMemo(() => {
        if (!plans.data?.items) return [];
        
        // Create a map to keep only unique plans by ID
        const planMap = new Map();
        
        plans.data.items.forEach(plan => {
            // Only add if not already in map
            if (!planMap.has(plan.id)) {
                planMap.set(plan.id, {
                    id: plan.id,
                    name: plan.name
                });
            }
        });
        
        // Convert back to array and sort by name
        return Array.from(planMap.values())
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [plans.data?.items]);

    const setStartDateUtcWithFirstSecond = (date: Date | undefined) => {
        if (date) {
            const midnightUtc = new Date(
                Date.UTC(
                    date.getUTCFullYear(),
                    date.getUTCMonth(),
                    date.getUTCDate(),
                    0,
                    0,
                    0,
                    0
                )
            );
            setStartDate(midnightUtc);
            // Update URL parameters with date only
            const newParams = new URLSearchParams(searchParams);
            newParams.set('fromDate', formatDateOnly(midnightUtc));
            setSearchParams(newParams);
        } else {
            setStartDate(getDefaultStartDate());
            // Remove fromDate from URL parameters
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('fromDate');
            setSearchParams(newParams);
        }
    };

    const setEndDateUtcWithLastSecond = (date: Date | undefined) => {
        if (date) {
            const lastSecondUtc = new Date(
                Date.UTC(
                    date.getUTCFullYear(),
                    date.getUTCMonth(),
                    date.getUTCDate(),
                    23,
                    59,
                    59,
                    999
                )
            )
            setEndDate(lastSecondUtc)
            // Update URL parameters with date only
            const newParams = new URLSearchParams(searchParams)
            newParams.set('toDate', formatDateOnly(lastSecondUtc))
            setSearchParams(newParams)
        } else {
            setEndDate(undefined)
            // Remove toDate from URL parameters
            const newParams = new URLSearchParams(searchParams)
            newParams.delete('toDate')
            setSearchParams(newParams)
        }
    }

    const handleSearch = (term: string) => {
        setSearchTerm(term)
        const newParams = new URLSearchParams(searchParams)
        if (term) {
            newParams.set('searchTerm', term)
        } else {
            newParams.delete('searchTerm')
        }
        setSearchParams(newParams)
    }

    const handlePlansChange = (planIds: string[]) => {
        setSelectedPlans(planIds)
        const newParams = new URLSearchParams(searchParams)
        if (planIds.length > 0) {
            newParams.set('planId', planIds.join(','))
        } else {
            newParams.delete('planId')
        }
        setSearchParams(newParams)
    }

    const excludedTypes = [TransactionType.BALANCE_EXPIRE]
    const excludeTypesParam = excludedTypes.join(',')

    const queryString = buildQueryString({
        assumeLocalTime: true,
        employerId: employer.data?.id,
        page: page,
        pageSize: pageSize,
        excludeTypes: excludeTypesParam,
        fromDate: formatDateForQuery(startDate),
        toDate: formatDateForQuery(endDate),
        searchTerm: searchTerm || undefined,
        planId: selectedPlans.length > 0 ? selectedPlans : undefined,
        status: [TransactionStatus.PENDING, TransactionStatus.APPROVED],
        type: [TransactionType.PURCHASE, TransactionType.REFUND, TransactionType.REIMBURSEMENT]
    })

    const transactions = useCustomQuery<{
        items: Transaction[]
        totalItems: number
    }>({
        endpoint: `/transactions?${queryString}`,
        options: {
            enabled: !!employer.data?.id
        }
    })

    // Add new query for export data
    const exportQueryString = buildQueryString({
        employerId: employer.data?.id,
        excludeTypes: excludeTypesParam,
        fromDate: formatDateForQuery(startDate),
        toDate: formatDateForQuery(endDate),
        searchTerm: searchTerm || undefined,
        planId: selectedPlans.length > 0 ? selectedPlans : undefined,
        status: [TransactionStatus.PENDING, TransactionStatus.APPROVED, TransactionStatus.DECLINED],
        type: [TransactionType.PURCHASE, TransactionType.REFUND, TransactionType.REIMBURSEMENT]
    })

    const exportTransactions = useCustomQuery<Transaction[]>({
        endpoint: `/transactions/export?${exportQueryString}`,
        options: {
            enabled: false // Only fetch when needed
        }
    })

    const handleExportCSV = async () => {
        await exportTransactions.refetch()
        if (exportTransactions.data) {
            const csv = convertTransactionsToCsv(
                exportTransactions.data,
                false,
                serviceSectors.data?.items,
                allEmployees.data?.items,
                tagCategories.data?.items,
                plans.data?.items,
                accounts.data?.items
            );
            downloadCsv(csv, `Transactions-${new Date().toLocaleString()}`);
        }
    };

    const handleTaxLabFbtExtract = async () => {
        await exportTransactions.refetch()
        if (exportTransactions.data) {
            const csv = convertTransactionsToTaxLabFbtCsv(exportTransactions.data, allEmployees.data?.items);
            downloadCsv(csv, `Transactions-TaxLab-${new Date().toLocaleString()}`);
        }
    };

    useEffect(() => {
        setHeaderActions([])
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        transactions.refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize, startDate, endDate, searchTerm, selectedPlans])

    if (
        transactions.isLoading ||
        employer.isLoading ||
        employees.isLoading ||
        accounts.isLoading ||
        serviceSectors.isLoading ||
        allEmployees.isLoading ||
        tagCategories.isLoading
    ) {
        return <LoadingSpinner />
    }

    return (
        <>
            <PageHeader pageTitle="Transactions" />
            <TransactionsTableControlBar
                startDate={startDate}
                endDate={endDate}
                updateStartDate={setStartDateUtcWithFirstSecond}
                updateEndDate={setEndDateUtcWithLastSecond}
                onSearch={handleSearch}
                searchTerm={searchTerm}
                plans={distinctPlans}
                selectedPlans={selectedPlans}
                onPlansChange={handlePlansChange}
                onExportCSV={handleExportCSV}
                onExportTaxLabFbt={handleTaxLabFbtExtract}
            />
            <TransactionsTable
                transactions={transactions.data?.items || []}
                totalItems={transactions.data?.totalItems || 0}
                page={page}
                pageSize={pageSize}
                updatePage={setPage}
                updatePageSize={setPageSize}
                isLoading={transactions.isLoading}
                employerId={employer.data?.id}
                employees={employees.data?.items}
                accounts={accounts.data?.items}
                serviceSectors={serviceSectors.data?.items}
            />
        </>
    )
}

export { Transactions }
